import { useMemo, useState } from 'react';

import {
  ActionsGroup,
  Bleed,
  Button,
  DialogV2,
  Inset,
  Switch,
  colorOrToken,
  makeDialog,
  notify,
  tokens,
  useCloseDialog,
  Typography,
  Stack,
} from '@cast/design-system';
import { Cloud } from '@cast/shared-types';
import { CloudAssetIntegrationsCommitmentStatus } from '@cast/types-inventory';
import { CommitmentCloud } from '@cast/types-pricing';

import { useUpdateCloudAssetIntegrationMutation } from 'hooks/mutations/inventory/useUpdateCloudAssetIntegrationMutation';

import { AUTO_ENABLE_SUPPORTED_PROVIDERS } from '../_constants';
import { CommitmentsCloudAssetIntegration } from '../_types';

import { CspIcon } from '@cast/shared-csp-icons-ui';

import { CSP_NAME_MAP } from '@cast/utils';

type CspToggleRowProps = {
  csp: Cloud;
  checked: boolean;
  onChange: (checked: boolean) => void;
  disabled?: boolean;
};

const CspToggleRow = ({
  csp,
  checked,
  onChange,
  disabled,
}: CspToggleRowProps) => {
  const cursor = !disabled ? 'pointer' : 'not-allowed';
  return (
    <Stack
      component="label"
      sx={{ cursor }}
      direction="row"
      alignItems="center"
      gap={8}
    >
      <Switch
        size="sm"
        checked={checked}
        onChange={(_, value) => onChange(value)}
        disabled={disabled}
        sx={{ '.DS-Switch-input': { cursor } }}
        testId={`csp-toggle-${csp.toLowerCase()}`}
      />
      <CspIcon csp={csp} size={16} />
      <Typography variant="P12M">{CSP_NAME_MAP[csp]}</Typography>
    </Stack>
  );
};

type CommitmentUploadSettingsDialogProps = {
  integrations: CommitmentsCloudAssetIntegration[];
};

const DISPLAYED_PROVIDERS = AUTO_ENABLE_SUPPORTED_PROVIDERS;

const providerToCloud: Record<CommitmentCloud, Cloud> = {
  AWS: Cloud.aws,
  GCP: Cloud.gcp,
  AZURE: Cloud.azure,
};

type ProviderState = Record<CommitmentCloud, boolean>;

const getInitialState = (
  integrations: CommitmentsCloudAssetIntegration[]
): ProviderState => {
  return integrations.reduce<ProviderState>(
    (toggleState, { provider, settings }) => {
      if (provider) {
        toggleState[provider] =
          settings?.commitments?.assignAutomatically ?? false;
      }
      return toggleState;
    },
    {
      AWS: false,
      GCP: false,
      AZURE: false,
    }
  );
};

const getIntegrationsByProvider = (
  integrations: CommitmentsCloudAssetIntegration[]
): Record<CommitmentCloud, CommitmentsCloudAssetIntegration[]> => {
  return integrations.reduce<
    Record<CommitmentCloud, CommitmentsCloudAssetIntegration[]>
  >(
    (result, integration) => {
      const provider = integration.provider;
      if (provider) {
        result[provider].push(integration);
      }
      return result;
    },
    {
      AWS: [],
      GCP: [],
      AZURE: [],
    }
  );
};

const CommitmentUploadSettingsDialogContent = ({
  integrations,
}: CommitmentUploadSettingsDialogProps) => {
  const closeDialog = useCloseDialog();
  const { mutateAsync } = useUpdateCloudAssetIntegrationMutation();
  const [isUpdating, setIsUpdating] = useState(false);

  const initialState = useMemo(
    () => getInitialState(integrations),
    [integrations]
  );

  const integrationsByProvider = useMemo(
    () => getIntegrationsByProvider(integrations),
    [integrations]
  );

  const availableProviders = useMemo(() => {
    return DISPLAYED_PROVIDERS.filter(
      (provider) => integrationsByProvider[provider].length > 0
    );
  }, [integrationsByProvider]);

  const [toggleState, setToggleState] = useState<ProviderState>(initialState);

  const hasChanges = useMemo(() => {
    return DISPLAYED_PROVIDERS.some(
      (provider) => toggleState[provider] !== initialState[provider]
    );
  }, [toggleState, initialState]);

  const handleToggle = (provider: CommitmentCloud, checked: boolean) => {
    setToggleState((prev) => ({ ...prev, [provider]: checked }));
  };

  const handleSave = async () => {
    setIsUpdating(true);

    const changedProviders = DISPLAYED_PROVIDERS.filter(
      (provider) => toggleState[provider] !== initialState[provider]
    );

    const updates = changedProviders.flatMap((provider) =>
      integrationsByProvider[provider].map((integration) => ({
        id: integration.id!,
        payload: {
          settings: {
            commitments: {
              assignAutomatically: toggleState[provider],
              defaultStatus: toggleState[provider]
                ? CloudAssetIntegrationsCommitmentStatus.ACTIVE
                : CloudAssetIntegrationsCommitmentStatus.INACTIVE,
            },
          },
        },
      }))
    );

    try {
      await Promise.all(updates.map((update) => mutateAsync(update)));
      notify.success('Commitment upload settings updated');
      closeDialog();
    } catch {
      // Do nothing, notification is posted via the onError callback
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <DialogV2
      testId="upload-settings-dialog"
      title="Commitment upload settings"
      endActions={
        <ActionsGroup>
          <Button
            variant="secondary"
            onClick={closeDialog}
            disabled={isUpdating}
            testId="cancel-button"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={!hasChanges || isUpdating}
            loading={isUpdating}
            testId="save-button"
          >
            Save
          </Button>
        </ActionsGroup>
      }
    >
      <Inset>
        <Typography variant="P14R" color="textSecondary" pb={16}>
          Configure default behavior for newly uploaded commitments.
        </Typography>
        <Bleed
          sx={{
            bgcolor: colorOrToken('grey.100', tokens.theme.bg.structural.page),
          }}
          py={16}
        >
          <Inset display="flex" gap={24}>
            <Stack gap={2} width={328}>
              <Typography variant="P14M">
                Auto-enable new commitments
              </Typography>
              <Typography variant="P12R" color="textSecondary">
                Newly uploaded commitments will be automatically enabled for
                autoscaling and assigned to all compatible clusters.
              </Typography>
            </Stack>
            <Stack gap={12} justifyContent="center" flex={1}>
              {availableProviders.map((provider) => (
                <CspToggleRow
                  key={provider}
                  csp={providerToCloud[provider]}
                  checked={toggleState[provider]}
                  onChange={(checked) => handleToggle(provider, checked)}
                  disabled={isUpdating}
                />
              ))}
            </Stack>
          </Inset>
        </Bleed>
      </Inset>
    </DialogV2>
  );
};

export const useCommitmentUploadSettingsDialog = makeDialog(
  CommitmentUploadSettingsDialogContent
);
